import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { useLocationListing } from '../hooks/useLocationListing';
import { LocationControls, DistanceBadge } from '../components/LocationControls';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Search,
  MapPin,
  Star,
  Clock,
  Users,
  Plus,
  CheckCircle,
  Loader2,
  Dog,
  Trees,
  Droplets,
  Fence,
  Sun,
  Car,
  Lamp,
  LogIn,
  LogOut,
  Filter,
  Navigation,
  Route,
  TreePine,
  Map,
  List
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import MapView from '../components/MapView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const amenityIcons = {
  has_fence: { icon: Fence, label: 'Fenced' },
  has_water: { icon: Droplets, label: 'Water' },
  has_shade: { icon: Sun, label: 'Shade' },
  has_parking: { icon: Car, label: 'Parking' },
  has_lighting: { icon: Lamp, label: 'Lighting' },
  has_small_dog_area: { icon: Dog, label: 'Small Dog Area' },
  is_off_leash: { icon: Dog, label: 'Off-Leash' },
};

const DogParks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Use the location listing hook for parks
  const {
    items: parks,
    loading,
    userLocation,
    locationLoading,
    requestLocation,
    clearLocation,
    sortBy,
    setSortBy,
    maxDistance,
    setMaxDistance,
    filters,
    updateFilter,
    refetch
  } = useLocationListing({
    endpoint: '/api/parks',
    buildParams: useCallback((filters) => {
      const params = {};
      if (filters.city) params.city = filters.city;
      if (filters.zip_code) params.zip_code = filters.zip_code;
      if (filters.state) params.state = filters.state;
      if (filters.search) params.search = filters.search;
      if (filters.has_fence === 'true') params.has_fence = true;
      return params;
    }, []),
    initialFilters: {
      city: '',
      zip_code: '',
      state: '',
      search: '',
      has_fence: ''
    }
  });

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedPark, setSelectedPark] = useState(null);
  const [myDogs, setMyDogs] = useState([]);
  const [selectedDogs, setSelectedDogs] = useState([]);
  const [activeCheckins, setActiveCheckins] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  
  // New park form
  const [newPark, setNewPark] = useState({
    name: '',
    description: '',
    city: '',
    state: '',
    zip_code: '',
    address: '',
    hours_of_operation: '',
    is_free: true,
    image_url: '',
    amenities: {
      has_fence: false,
      has_water: false,
      has_shade: false,
      has_parking: false,
      has_lighting: false,
      has_small_dog_area: false,
      is_off_leash: true,
      surface_type: 'grass'
    }
  });

  useEffect(() => {
    if (user) {
      fetchMyDogs();
      fetchMyActiveCheckins();
    }
  }, [user]);

  const fetchMyDogs = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/dogs`);
      setMyDogs(response.data);
    } catch (error) {
      console.error('Error fetching dogs:', error);
    }
  };

  const fetchMyActiveCheckins = async () => {
    try {
      const response = await axios.get(`${API}/users/me/checkins?active_only=true`);
      const checkins = {};
      response.data.forEach(c => {
        checkins[c.park_id] = c;
      });
      setActiveCheckins(checkins);
    } catch (error) {
      console.error('Error fetching checkins:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  const handleCreatePark = async () => {
    if (!newPark.name || !newPark.city) {
      toast.error('Please fill in park name and city');
      return;
    }
    
    try {
      await axios.post(`${API}/parks`, newPark);
      toast.success('Dog park added!');
      setAddDialogOpen(false);
      setNewPark({
        name: '',
        description: '',
        city: '',
        state: '',
        address: '',
        hours_of_operation: '',
        is_free: true,
        image_url: '',
        amenities: {
          has_fence: false,
          has_water: false,
          has_shade: false,
          has_parking: false,
          has_lighting: false,
          has_small_dog_area: false,
          is_off_leash: true,
          surface_type: 'grass'
        }
      });
      fetchParks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add park');
    }
  };

  const handleCheckin = async () => {
    if (!selectedPark) return;
    
    try {
      await axios.post(`${API}/parks/${selectedPark.id}/checkin`, {
        dog_ids: selectedDogs
      });
      toast.success(`Checked in to ${selectedPark.name}!`);
      setCheckinDialogOpen(false);
      setSelectedDogs([]);
      refetch();
      fetchMyActiveCheckins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    }
  };

  const handleCheckout = async (parkId) => {
    try {
      await axios.post(`${API}/parks/${parkId}/checkout`);
      toast.success('Checked out!');
      refetch();
      fetchMyActiveCheckins();
    } catch (error) {
      toast.error('Failed to check out');
    }
  };

  const renderStars = (rating) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'
          }`}
        />
      ))}
    </div>
  );

  const renderAmenities = (amenities) => {
    if (!amenities) return null;
    
    // Handle array format from seeded data
    if (Array.isArray(amenities)) {
      const amenityLabels = {
        fenced: { icon: Fence, label: 'Fenced' },
        water_fountain: { icon: Droplets, label: 'Water' },
        seating: { icon: Sun, label: 'Seating' },
        shade: { icon: Trees, label: 'Shade' },
        small_dog_area: { icon: Dog, label: 'Small Dogs' },
        large_dog_area: { icon: Dog, label: 'Large Dogs' },
        parking: { icon: Car, label: 'Parking' },
        trails: { icon: Trees, label: 'Trails' },
        agility_equipment: { icon: Dog, label: 'Agility' },
        pond: { icon: Droplets, label: 'Pond' },
        lake_access: { icon: Droplets, label: 'Lake' },
        beach: { icon: Sun, label: 'Beach' },
        swimming: { icon: Droplets, label: 'Swimming' },
        scenic_views: { icon: Sun, label: 'Views' },
        double_gated: { icon: Fence, label: 'Double Gate' },
        grass: { icon: Trees, label: 'Grass' },
        turf: { icon: Trees, label: 'Turf' },
        dog_wash: { icon: Droplets, label: 'Dog Wash' },
        showers: { icon: Droplets, label: 'Showers' },
        splash_pad: { icon: Droplets, label: 'Splash Pad' }
      };
      
      return (
        <div className="flex flex-wrap gap-1">
          {amenities.slice(0, 5).map((amenity) => {
            const info = amenityLabels[amenity] || { icon: CheckCircle, label: amenity.replace('_', ' ') };
            const Icon = info.icon;
            return (
              <Badge key={amenity} variant="outline" className="flex items-center gap-1 text-xs">
                <Icon className="w-3 h-3" />
                {info.label}
              </Badge>
            );
          })}
          {amenities.length > 5 && (
            <Badge variant="outline" className="text-xs">+{amenities.length - 5}</Badge>
          )}
        </div>
      );
    }
    
    // Handle object format
    return (
      <div className="flex flex-wrap gap-2">
        {Object.entries(amenityIcons).map(([key, { icon: Icon, label }]) => {
          if (amenities[key]) {
            return (
              <Badge key={key} variant="outline" className="flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {label}
              </Badge>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Dog Parks"
        description="Find dog parks near you. Reviews, amenities, busy hours, and check-ins from the AADC community."
        url="/parks"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-4xl font-heading font-medium flex items-center gap-3">
              <Trees className="w-10 h-10" />
              Find Dog Parks Near You
            </h1>
            <GuestAccessibleBadge className="bg-white/20 text-white" />
          </div>
          <p className="text-white/80 text-lg mb-8 max-w-2xl">
            Discover the best dog parks in your area. Check-in, see who's there, 
            and find the perfect spot for your pup to play!
          </p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search parks..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="pl-12 h-12 bg-white text-foreground"
                data-testid="park-search"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="City"
                value={filters.city}
                onChange={(e) => updateFilter('city', e.target.value)}
                className="pl-12 h-12 w-48 bg-white text-foreground"
              />
            </div>
            <Button type="submit" className="h-12 px-8 bg-amber-500 hover:bg-amber-600">
              Search
            </Button>
          </form>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to check in, add parks, and see who's at the park" />
        
        {/* Header with Actions */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <p className="text-muted-foreground">
              {loading ? 'Loading...' : `${parks.length} dog parks found`}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <LocationControls
              userLocation={userLocation}
              locationLoading={locationLoading}
              onRequestLocation={requestLocation}
              onClearLocation={clearLocation}
              maxDistance={maxDistance}
              onMaxDistanceChange={setMaxDistance}
              sortBy={sortBy}
              onSortByChange={setSortBy}
            />
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
            {/* Map/List Toggle */}
            <div className="flex rounded-lg border overflow-hidden">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('list')}
                data-testid="parks-list-view-btn"
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('map')}
                data-testid="parks-map-view-btn"
              >
                <Map className="w-4 h-4 mr-1" />
                Map
              </Button>
            </div>
            {user && (
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-green-600 hover:bg-green-700" data-testid="add-park-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add a Dog Park
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Add a Dog Park</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <Input
                      placeholder="Park Name *"
                      value={newPark.name}
                      onChange={(e) => setNewPark({ ...newPark, name: e.target.value })}
                    />
                    
                    <Textarea
                      placeholder="Description"
                      value={newPark.description}
                      onChange={(e) => setNewPark({ ...newPark, description: e.target.value })}
                      rows={3}
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="City *"
                        value={newPark.city}
                        onChange={(e) => setNewPark({ ...newPark, city: e.target.value })}
                      />
                      <Input
                        placeholder="State"
                        value={newPark.state}
                        onChange={(e) => setNewPark({ ...newPark, state: e.target.value })}
                      />
                    </div>
                    
                    <Input
                      placeholder="Address"
                      value={newPark.address}
                      onChange={(e) => setNewPark({ ...newPark, address: e.target.value })}
                    />
                    
                    <Input
                      placeholder="Hours (e.g., Dawn to Dusk)"
                      value={newPark.hours_of_operation}
                      onChange={(e) => setNewPark({ ...newPark, hours_of_operation: e.target.value })}
                    />
                    
                    <Input
                      placeholder="Image URL"
                      value={newPark.image_url}
                      onChange={(e) => setNewPark({ ...newPark, image_url: e.target.value })}
                    />
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">Amenities</label>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(amenityIcons).map(([key, { label }]) => (
                          <label key={key} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newPark.amenities[key] || false}
                              onChange={(e) => setNewPark({
                                ...newPark,
                                amenities: { ...newPark.amenities, [key]: e.target.checked }
                              })}
                              className="rounded"
                            />
                            <span className="text-sm">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700" 
                      onClick={handleCreatePark}
                    >
                      Add Dog Park
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1 block">City</label>
                  <Input
                    placeholder="City name..."
                    value={filters.city}
                    onChange={(e) => updateFilter('city', e.target.value)}
                  />
                </div>
                <div className="w-32">
                  <label className="text-sm font-medium mb-1 block">State</label>
                  <Input
                    placeholder="State..."
                    value={filters.state}
                    onChange={(e) => updateFilter('state', e.target.value)}
                    maxLength={2}
                  />
                </div>
                <div className="w-32">
                  <label className="text-sm font-medium mb-1 block">Zip Code</label>
                  <Input
                    placeholder="Zip..."
                    value={filters.zip_code}
                    onChange={(e) => updateFilter('zip_code', e.target.value)}
                    maxLength={5}
                  />
                </div>
                <Select value={filters.has_fence || "any"} onValueChange={(val) => updateFilter('has_fence', val === "any" ? "" : val)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Fenced?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">Fenced Only</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={refetch} className="bg-green-600 hover:bg-green-700">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                <Button variant="outline" onClick={() => {
                  updateFilter('has_fence', '');
                  updateFilter('city', '');
                  updateFilter('zip_code', '');
                  updateFilter('state', '');
                  updateFilter('search', '');
                  refetch();
                }}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parks List/Map */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : parks.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Trees className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">No dog parks found</h3>
              <p className="text-muted-foreground mb-4">
                Be the first to add a dog park in your area!
              </p>
              {user && (
                <Button onClick={() => setAddDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
                  Add a Dog Park
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'map' ? (
          /* Map View */
          <MapView
            height="550px"
            dataTestId="parks-map-view"
            markers={parks.map(park => ({
              id: park.id,
              lat: park.latitude,
              lng: park.longitude,
              title: park.name,
              color: '#16a34a',
              description: `${park.city}${park.state ? ', ' + park.state : ''}`,
              park,
            }))}
            renderPopup={(marker) => (
              <div className="min-w-[200px]">
                <h3 className="font-bold text-sm mb-1">{marker.park.name}</h3>
                <p className="text-xs text-gray-500 mb-1">
                  {marker.park.city}{marker.park.state ? `, ${marker.park.state}` : ''}
                </p>
                {(marker.park.average_rating || marker.park.rating) > 0 && (
                  <p className="text-xs mb-1">
                    Rating: {(marker.park.average_rating || marker.park.rating || 0).toFixed(1)} / 5
                  </p>
                )}
                {(marker.park.hours_of_operation || marker.park.hours) && (
                  <p className="text-xs text-gray-500 mb-2">{marker.park.hours_of_operation || marker.park.hours}</p>
                )}
                <button
                  onClick={() => navigate(`/parks/${marker.park.id}`)}
                  className="text-xs text-green-600 font-medium hover:underline"
                >
                  View Details &rarr;
                </button>
              </div>
            )}
          />
        ) : (
          /* List View */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parks.map((park) => (
              <Card 
                key={park.id} 
                className="card-interactive overflow-hidden"
                data-testid={`park-card-${park.id}`}
              >
                {/* Image */}
                <div className="h-48 bg-muted relative">
                  {park.image_url ? (
                    <img 
                      src={park.image_url} 
                      alt={park.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200">
                      <Trees className="w-16 h-16 text-green-600/50" />
                    </div>
                  )}
                  
                  {/* Current visitors badge */}
                  {park.current_visitors > 0 && (
                    <Badge className="absolute top-3 right-3 bg-green-600">
                      <Users className="w-3 h-3 mr-1" />
                      {park.current_visitors} here now
                    </Badge>
                  )}
                  
                  {/* Free badge */}
                  {park.is_free && (
                    <Badge className="absolute top-3 left-3 bg-blue-600">
                      Free
                    </Badge>
                  )}
                </div>
                
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading text-lg font-medium">{park.name}</h3>
                  </div>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-3">
                    {renderStars(park.average_rating || park.rating || 0)}
                    <span className="font-medium">{(park.average_rating || park.rating || 0).toFixed(1)}</span>
                    <span className="text-muted-foreground text-sm">
                      ({park.review_count || 0} reviews)
                    </span>
                  </div>
                  
                  {/* Location & Distance */}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground text-sm flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {park.city}{park.state && `, ${park.state}`}{park.zip_code && ` ${park.zip_code}`}
                    </p>
                    <DistanceBadge distance={park.distance_miles} />
                  </div>
                  
                  {/* Size */}
                  {park.size_acres && (
                    <p className="text-muted-foreground text-sm mb-2">
                      {park.size_acres} acres
                    </p>
                  )}
                  
                  {/* Hours */}
                  {(park.hours_of_operation || park.hours) && (
                    <p className="text-muted-foreground text-sm flex items-center gap-1 mb-3">
                      <Clock className="w-4 h-4" />
                      {park.hours_of_operation || park.hours}
                    </p>
                  )}
                  
                  {/* Amenities */}
                  <div className="mb-4">
                    {renderAmenities(park.amenities)}
                  </div>
                  
                  {/* Check-in stats */}
                  <p className="text-xs text-muted-foreground mb-4">
                    {park.checkin_count} total check-ins
                  </p>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => navigate(`/parks/${park.id}`)}
                    >
                      View Details
                    </Button>
                    {user && (
                      activeCheckins[park.id] ? (
                        <Button 
                          className="flex-1 bg-red-500 hover:bg-red-600"
                          onClick={() => handleCheckout(park.id)}
                        >
                          <LogOut className="w-4 h-4 mr-1" />
                          Check Out
                        </Button>
                      ) : (
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setSelectedPark(park);
                            setCheckinDialogOpen(true);
                          }}
                        >
                          <LogIn className="w-4 h-4 mr-1" />
                          Check In
                        </Button>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Check-in Dialog */}
      <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              Check in to {selectedPark?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {myDogs.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Which dogs are you bringing?
                </label>
                <div className="space-y-2">
                  {myDogs.map((dog) => (
                    <label key={dog.id} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={selectedDogs.includes(dog.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDogs([...selectedDogs, dog.id]);
                          } else {
                            setSelectedDogs(selectedDogs.filter(id => id !== dog.id));
                          }
                        }}
                        className="rounded"
                      />
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={dog.image_url} />
                        <AvatarFallback><Dog className="w-4 h-4" /></AvatarFallback>
                      </Avatar>
                      <span>{dog.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <Button 
              className="w-full bg-green-600 hover:bg-green-700" 
              onClick={handleCheckin}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Check In Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suggest a Park CTA */}
      <div className="container mx-auto px-4 pb-12">
        <Card className="bg-green-50/50 border-green-200">
          <CardContent className="p-6 text-center">
            <TreePine className="w-10 h-10 mx-auto text-green-600 mb-3" />
            <h3 className="font-heading font-medium text-lg mb-2">Know a Park We're Missing?</h3>
            <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
              Help us grow our dog park directory! Submit a park you love and we'll add it to the map.
            </p>
            <Button asChild className="bg-green-600 hover:bg-green-700">
              <Link to="/submit-park">
                <Plus className="w-4 h-4 mr-2" />
                Suggest a Dog Park
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DogParks;
